import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AccountCreditPolicy, ApiError } from '../api';
import { settingsEndpoints } from '../api';
import { Button, ErrorNote, Panel } from '../ui';
import { INPUT } from '../pages/event/shared';
import { dateWords } from '../terms';

/**
 * The account-credit policy (D-372): whether credit expires, and whether staff
 * may adjust a balance. The owner's defaults are "never" and "yes"; they are a
 * setting so that changing either is a decision here rather than a deploy.
 * There is no cash-out switch because nothing cashes credit out on its own:
 * taking an issued credit back to the card is a person's decision on the order.
 */
export function AccountCreditPolicyPanel() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['settings', 'account-credit'], queryFn: settingsEndpoints.accountCredit });
  const [draft, setDraft] = useState<AccountCreditPolicy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const current = draft ?? query.data?.policy ?? null;
  const save = useMutation({
    mutationFn: (input: AccountCreditPolicy) => settingsEndpoints.updateAccountCredit(input),
    onSuccess: (result) => {
      setError(null);
      setDraft(null);
      setNotice(result.policy.expires_after_days ? `Unspent credit now expires after ${result.policy.expires_after_days} days.` : 'Credit does not expire.');
      void queryClient.invalidateQueries({ queryKey: ['settings', 'account-credit'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  if (!current) return null;
  const expires = current.expires_after_days !== null;
  return (
    <Panel title="Account credit" description={`Credit a customer holds on their account: returned flexible tickets, a canceled ${dateWords().one} in a package, and the window's give-back-as-credit. It is spent at checkout, on a payment link and at the window.`}>
      <div className="space-y-3 p-5">
        {error ? <ErrorNote>{error}</ErrorNote> : null}
        {notice ? <p className="text-sm text-ink-600">{notice}</p> : null}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={expires} onChange={(e) => setDraft({ ...current, expires_after_days: e.target.checked ? 365 : null })} className="h-4 w-4 accent-brand" />
          Unspent credit expires
          {expires ? (
            <>
              <span className="text-ink-500">after</span>
              <input type="number" min={30} max={3650} value={current.expires_after_days ?? 365} onChange={(e) => setDraft({ ...current, expires_after_days: Math.max(30, Number(e.target.value || 30)) })} className={`${INPUT} w-24`} />
              <span className="text-ink-500">days, oldest first</span>
            </>
          ) : (
            <span className="text-ink-500">(off: credit never expires)</span>
          )}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={current.staff_adjustments} onChange={(e) => setDraft({ ...current, staff_adjustments: e.target.checked })} className="h-4 w-4 accent-brand" />
          Staff with “adjust account credit” may add to or take from a balance, with a reason
        </label>
        <div className="flex items-center gap-3">
          <Button onClick={() => save.mutate(current)} disabled={save.isPending || !draft}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {draft ? (
            <button type="button" className="text-sm text-ink-500 underline" onClick={() => { setDraft(null); setNotice(null); }}>
              Discard
            </button>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}
